import React, { useEffect, useMemo, useState, useRef } from 'react';
/**
 * MatchingRenderer — Connect left items to right items by tapping.
 *
 * DDD co-locatie: moved from components/practice/renderers/ in s68.
 */
import { QuestionRendererProps } from './types';
import { LatexRenderer } from '../../../components/ui/LatexRenderer';
import { motion, AnimatePresence } from 'framer-motion';
import { shuffleWithSeed } from '../../../utils/deterministicShuffle';
export function MatchingRenderer({
  question,
  onAnswer,
  onReadyChange,
  shouldValidate
}: QuestionRendererProps) {
  const config = question.data || {};
  // Normalize pairs from multiple data formats
  let pairs: Array<{
    left: string;
    right: string;
  }> = [];
  const rawPairs = config.pairs || config.matching?.pairs;
  if (Array.isArray(rawPairs) && rawPairs.length > 0) {
    pairs = rawPairs;
  } else if (Array.isArray(config.leftItems) && Array.isArray(config.rightItems) && config.correctMatches) {
    const matches = config.correctMatches as Record<string, number>;
    pairs = config.leftItems.map((left: string, i: number) => ({
      left,
      right: config.rightItems[matches[String(i)] ?? i] ?? ''
    }));
  }
  const prevValidateRef = useRef(false);
  const leftItems = useMemo(() => pairs.map((p, i) => ({
    id: i,
    content: typeof p.left === 'string' ? p.left : String(p.left ?? '')
  })), [pairs]);
  const rightItems = useMemo(() => {
    const items = pairs.map((p, i) => ({
      id: i,
      content: typeof p.right === 'string' ? p.right : String(p.right ?? '')
    }));
    try {
      return shuffleWithSeed(items, question.id || 'fallback-seed').shuffled;
    } catch {
      return items;
    }
  }, [pairs, question.id]);
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [connections, setConnections] = useState<Record<number, number>>({});
  const [justConnected, setJustConnected] = useState<number | null>(null);
  useEffect(() => {
    setSelectedLeft(null);
    setConnections({});
    setJustConnected(null);
    prevValidateRef.current = false;
  }, [question.id]);
  useEffect(() => {
    if (!shouldValidate) prevValidateRef.current = false;
  }, [shouldValidate]);
  useEffect(() => {
    onReadyChange?.(Object.keys(connections).length === leftItems.length);
  }, [connections, leftItems.length, onReadyChange]);
  // CRITICAL: Use rAF to decouple onAnswer from render cycle (framer-motion fix)
  useEffect(() => {
    if (shouldValidate && !prevValidateRef.current) {
      prevValidateRef.current = true;
      const payload = JSON.stringify(Object.entries(connections).map(([l, r]) => [Number(l), r]));
      requestAnimationFrame(() => onAnswer(payload));
    }
  }, [shouldValidate, connections, onAnswer]);
  // Guard: no pairs — rendered AFTER all hooks
  if (pairs.length === 0) {
    return <div className="text-center p-8 bg-amber-50 rounded-xl border-2 border-amber-200" data-id="element-7099">
        <p className="text-amber-700 font-bold" data-id="element-7100">
          ⚠️ Geen koppelparen beschikbaar
        </p>
        <p className="text-sm text-amber-600 mt-2" data-id="element-7101">
          Deze vraag heeft geen geldige koppeldata.
        </p>
      </div>;
  }
  const handleLeftClick = (id: number) => {
    if (connections[id] !== undefined) {
      const next = {
        ...connections
      };
      delete next[id];
      setConnections(next);
    }
    setSelectedLeft(id);
  };
  const handleRightClick = (id: number) => {
    if (selectedLeft === null) return;
    const existingLeft = Object.keys(connections).find(k => connections[Number(k)] === id);
    const next = {
      ...connections
    };
    if (existingLeft) delete next[Number(existingLeft)];
    next[selectedLeft] = id;
    setConnections(next);
    setJustConnected(selectedLeft);
    setTimeout(() => setJustConnected(null), 400);
    setSelectedLeft(null);
  };
  const handleLeftKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`left-item-${(index + 1) % leftItems.length}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`left-item-${(index - 1 + leftItems.length) % leftItems.length}`)?.focus();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      document.getElementById('right-item-0')?.focus();
    }
  };
  const handleRightKeyDown = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      document.getElementById(`right-item-${(index + 1) % rightItems.length}`)?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      document.getElementById(`right-item-${(index - 1 + rightItems.length) % rightItems.length}`)?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      document.getElementById('left-item-0')?.focus();
    }
  };
  const getPairNumber = (leftId: number): number | null => {
    const sorted = Object.keys(connections).map(Number).sort((a, b) => a - b);
    const idx = sorted.indexOf(leftId);
    return idx !== -1 ? idx + 1 : null;
  };
  const getRightPairInfo = (rightId: number) => {
    const leftId = Object.keys(connections).find(k => connections[Number(k)] === rightId);
    if (leftId === undefined) return null;
    const pairNum = getPairNumber(Number(leftId));
    return pairNum !== null ? {
      leftId: Number(leftId),
      pairNum
    } : null;
  };
  const matchedCount = Object.keys(connections).length;
  return <div className="w-full max-w-2xl mx-auto" data-id="element-7102">
      <div className="flex items-center justify-between mb-5 px-1" data-id="element-7103">
        <p className="text-slate-400 text-xs font-medium" data-id="element-7104">
          Kies links, dan rechts
        </p>
        <div className="flex items-center gap-2" data-id="element-7105">
          <div className="flex gap-1.5" data-id="element-7106">
            {leftItems.map(item => <motion.div key={item.id} initial={false} animate={{
            scale: connections[item.id] !== undefined ? 1 : 0.75
          }} className={`w-2 h-2 rounded-full transition-colors duration-300 ${connections[item.id] !== undefined ? 'bg-indigo-400' : 'bg-slate-200'}`} data-id="element-7107" />)}
          </div>
          <span className="text-xs font-semibold text-slate-400 tabular-nums" data-id="element-7108">
            {matchedCount}/{leftItems.length}
          </span>
        </div>
      </div>

      <div className="flex gap-3 sm:gap-5" data-id="element-7109">
        {/* Left Column */}
        <div className="flex-1 space-y-2.5" role="listbox" aria-label="Linker items" data-id="element-7110">
          {leftItems.map((item, index) => {
          const isConnected = connections[item.id] !== undefined;
          const isSelected = selectedLeft === item.id;
          const pairNum = getPairNumber(item.id);
          const wasJust = justConnected === item.id;
          return <motion.button key={item.id} id={`left-item-${index}`} role="option" aria-selected={isSelected || isConnected} tabIndex={0} onClick={() => handleLeftClick(item.id)} onKeyDown={e => handleLeftKeyDown(e, index)} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0,
            scale: wasJust ? [1, 1.04, 1] : 1
          }} transition={{
            opacity: {
              delay: index * 0.05,
              duration: 0.3
            },
            x: {
              delay: index * 0.05,
              duration: 0.3
            },
            scale: {
              duration: 0.3
            }
          }} whileTap={{
            scale: 0.97
          }} className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border-2 text-left transition-colors relative focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 min-h-[44px] flex items-center ${isSelected ? 'border-indigo-500 bg-indigo-50 shadow-md shadow-indigo-100/50 z-10' : isConnected ? 'border-indigo-200 bg-indigo-50/80' : 'border-slate-200 bg-white hover:border-slate-300 active:bg-slate-50'}`} data-id="element-7111">
                {isConnected && <motion.div initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} className="absolute left-0 top-2 bottom-2 w-1 bg-indigo-400 rounded-r-full" data-id="element-7112" />}
                <div className={`text-sm sm:text-base font-medium flex-1 ${isConnected ? 'text-indigo-700 pl-1.5' : 'text-slate-700'}`} data-id="element-7113">
                  <LatexRenderer content={item.content} data-id="element-7114" />
                </div>
                <AnimatePresence data-id="element-7115">
                  {pairNum && <motion.span initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className="ml-2 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center" data-id="element-7116">
                      {pairNum}
                    </motion.span>}
                </AnimatePresence>
                {isSelected && <motion.div className="absolute inset-0 rounded-xl border-2 border-indigo-500 pointer-events-none" initial={false} animate={{
              boxShadow: ['0 0 0 0 rgba(99,102,241,0.3)', '0 0 0 5px rgba(99,102,241,0)', '0 0 0 0 rgba(99,102,241,0.3)']
            }} transition={{
              boxShadow: {
                duration: 1.5,
                repeat: Infinity
              }
            }} data-id="element-7117" />}
              </motion.button>;
        })}
        </div>

        {/* Center divider */}
        <div className="flex flex-col items-center justify-center py-2" data-id="element-7118">
          <div className="w-px flex-1 bg-gradient-to-b from-transparent via-slate-200 to-transparent" data-id="element-7119" />
        </div>

        {/* Right Column */}
        <div className="flex-1 space-y-2.5" role="listbox" aria-label="Rechter items" data-id="element-7120">
          {rightItems.map((item, index) => {
          const pairInfo = getRightPairInfo(item.id);
          const isConnected = pairInfo !== null;
          const isTarget = selectedLeft !== null && !isConnected;
          return <motion.button key={item.id} id={`right-item-${index}`} role="option" aria-selected={isConnected} tabIndex={0} onClick={() => handleRightClick(item.id)} onKeyDown={e => handleRightKeyDown(e, index)} initial={{
            opacity: 0,
            x: 12
          }} animate={{
            opacity: 1,
            x: 0,
            scale: isTarget ? [1, 1.015, 1] : 1
          }} transition={{
            opacity: {
              delay: index * 0.05 + 0.1,
              duration: 0.3
            },
            x: {
              delay: index * 0.05 + 0.1,
              duration: 0.3
            },
            scale: isTarget ? {
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut'
            } : {
              duration: 0.2
            }
          }} whileTap={{
            scale: 0.97
          }} className={`w-full px-3 py-2.5 sm:px-4 sm:py-3 rounded-xl border-2 text-left transition-colors relative focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 min-h-[44px] flex items-center ${isConnected ? 'border-indigo-200 bg-indigo-50/80' : isTarget ? 'border-indigo-300 bg-indigo-50/60 shadow-sm shadow-indigo-100/50' : 'border-slate-200 bg-white hover:border-slate-300 active:bg-slate-50'}`} data-id="element-7121">
                <AnimatePresence data-id="element-7122">
                  {pairInfo && <motion.span initial={{
                scale: 0,
                opacity: 0
              }} animate={{
                scale: 1,
                opacity: 1
              }} exit={{
                scale: 0,
                opacity: 0
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} className="mr-2 flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500 text-white text-[10px] font-bold flex items-center justify-center" data-id="element-7123">
                      {pairInfo.pairNum}
                    </motion.span>}
                </AnimatePresence>
                <div className={`text-sm sm:text-base font-medium flex-1 ${isConnected ? 'text-indigo-700' : 'text-slate-700'}`} data-id="element-7124">
                  <LatexRenderer content={item.content} data-id="element-7125" />
                </div>
                {isConnected && <motion.div initial={{
              scaleY: 0
            }} animate={{
              scaleY: 1
            }} className="absolute right-0 top-2 bottom-2 w-1 bg-indigo-400 rounded-l-full" data-id="element-7126" />}
                {isTarget && <motion.div className="absolute inset-0 rounded-xl pointer-events-none" animate={{
              boxShadow: ['inset 0 0 0 0 rgba(99,102,241,0.06)', 'inset 0 0 12px 0 rgba(99,102,241,0.08)', 'inset 0 0 0 0 rgba(99,102,241,0.06)']
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-7127" />}
              </motion.button>;
        })}
        </div>
      </div>
    </div>;
}